/*jshint bitwise:false */
define('bindingHandlers/barChart',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'lib/d3',

    'util/domUtils',
    'util/d3Utils',

    'bindingHandlers/dataViz/components/svgContainer',

    'bindingHandlers/scrolled',
    'extensions/i18n/knockout.i18n'
], function (
    $,
    _,
    ko,
    d3,
    domUtils,
    d3Utils,

    renderSvgContainer
) {
    'use strict';

    var CANVAS_CLASS = "chart-canvas";

    // ===================================================================================
    // Chart Dimensions
    // ===================================================================================

    var BAR_HEIGHT = 20;
    var BAR_MARGIN = {
        right: 5,
        bottom: 10
    };

    var LABEL_WIDTH = 130;
    var LABEL_MARGIN = 10;
    var LABEL_TICK_LINE_LENGTH = 6;

    var VALUE_TEXT_FONT_SIZE = "13px"; // declare the value's font size here so that we
                                       // can get the correct width of the text when
                                       // computing its width
    var VALUE_TEXT_MARGIN = {
        horizontal: 5
    };

    var X_AXIS_HEIGHT = 20;

    // ===================================================================================
    // Utility Functions
    // ===================================================================================

    var getValueFromDataObject = function (dataObj, key) {
        if (key) {
            return ko.unwrap(dataObj[key]);
        }

        return dataObj;
    };

    var getCategoryTextFromDataObject = function (dataObj, key) {
        if (key) {
            return ko.unwrap(dataObj[key]);
        }

        return null;
    };

    // ===================================================================================
    // Content Rendering
    // ===================================================================================

    var renderLabels = function (svg, dataCollection, yScale, bindingData) {
        var getLabel = function (dataObj) {
            return getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);
        };

        var labelClasses = {
            "chart-label": true
        };

        // Join new data with existing data
        var labels = svg.selectAll(".chart-label")
                            .data(dataCollection);

        //
        // Create new labels
        //
        var labelsEnter = labels.enter();

        if (bindingData.categoryHrefKey){
            labelsEnter = labelsEnter
                .append("a")
                .attr("xlink:href", function (dataObj) {
                    return ko.unwrap(dataObj[bindingData.categoryHrefKey]);
                })
                .attr("target", "_parent");
        }

        labelsEnter
            .append("text")
            .classed(labelClasses) // conditionally set the label's classes
            .attr("x", 0)
            .attr("y", function (dataObj) {
                var label = getLabel(dataObj);
                return yScale(label) + yScale.rangeBand()/2;
            })
            .attr("dy", ".35em")
            .attr("text-anchor", "start")
            .attr("data-bind", function (dataObj) {
                return "tooltip: \"" + getLabel(dataObj) + "\"";
            })
            .text(d3Utils.truncateText(
                    function (dataObj) {
                        return getLabel(dataObj);
                    },
                    function () {
                        return LABEL_WIDTH;
                    }
                )
            );

        //
        // Update Existing Labels
        //
        labels.style("fill-opacity", 1);

        //
        // Remove Old Labels
        //
        labels.exit()
                .remove();
    };

    var renderYAxisLine = function (svg, xLocation, height) {
        return svg.append("line")
                .attr("class", "y-axis-line")
                .attr("x1", xLocation)
                .attr("y1", 0)
                .attr("x2", xLocation)
                .attr("y2", height)
                .attr("transform", "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + ",0)");
    };

    var renderVerticalLines = function (svg, height, xScale) {
        var minValue = d3.min(xScale.domain());

        if (svg.select(".y-axis-line").empty()) {
            renderYAxisLine(svg, 0, height);

            if (minValue < 0) {
                var splitLineNode = renderYAxisLine(svg, xScale(0), height);
                splitLineNode.classed("split-line", true);
            }
        } else if (minValue >= 0) {
            svg.select(".split-line").remove();
        }

        svg.selectAll(".y-axis-line")
            .attr("y2", height);
    };

    var renderBars = function (svg, dataCollection, xScale, yScale, bindingData) {
        // ===============================================================================
        // Utility Functions for Bar Rendering
        // ===============================================================================

        var generateYValue = function (dataObj) {
            var value = getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);
            return yScale(value) + yScale.rangeBand()/2;
        };

        var generateXValue = function (datum) {
            var value = getValueFromDataObject(datum, bindingData.valueKey);

            if (!_.hasValue(value)) {
                value = 0;
            }

            return value;
        };

        var getValueText = function (value, options) {
            if (!_.hasValue(value)) {
                return "";
            }

            if (options && options.formatValueText) {
                return options.formatValueText(value);
            }

            return ko.i18n.format(value, "n0");
        };

        // ===============================================================================
        // Bar Rendering Logic
        // ===============================================================================

        // Join new data with existing data
        var bars = svg.selectAll(".bar")
                        .data(dataCollection);

        //
        // Create New Bars
        //

        // Create the root node that each bar will live in
        var barEnter = bars.enter()
                            .append("g")
                                .attr("class", "bar")
                                .attr("transform", "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + ",0)")
                                .style("fill-opacity", 0);

        // Create the tick mark on the y-axis for the bar
        barEnter.append("line")
            .attr("x1", 0)
            .attr("x2", LABEL_TICK_LINE_LENGTH * -1)
            .attr("y1", generateYValue)
            .attr("y2", generateYValue);

        // Create the row's bar
        barEnter.append("rect")
            .attr("y", function (dataObj) {
                var value = getCategoryTextFromDataObject(dataObj, bindingData.categoryTextKey);
                return yScale(value);
            })
            .attr("height", yScale.rangeBand());

        // Create the text values for the bars
        barEnter.append("text")
            .attr("font-size", VALUE_TEXT_FONT_SIZE)
            .attr("y", generateYValue)
            .attr("dy", ".35em")
            .attr('class', 'value');

        //
        // Update Existing Bars
        //

        bars.classed({
            "bar": true,
            "positive": function (datum) {
                var value = generateXValue(datum);

                return value >= 0;
            },
            "negative": function (datum) {
                var value = generateXValue(datum);

                return value < 0;
            }
        });

        var barUpdate = bars.transition()
                .duration(250)
                .style("fill-opacity", 1);

        barUpdate.select("rect")
            .attr("x", function (datum) {
                var value = generateXValue(datum);

                return xScale(Math.min(0, value));
            })
            .attr("width", function (datum) {
                var value = generateXValue(datum);

                return Math.abs(xScale(value) - xScale(0));
            });

        barUpdate.select(".value")
            .text(function(datum) {
                var rawValue = getValueFromDataObject(datum, bindingData.valueKey);

                return getValueText(rawValue, bindingData);
            })
            .attr("x", function (datum) {
                var xValue = generateXValue(datum);
                var rawValue = getValueFromDataObject(datum, bindingData.valueKey);
                var valueText = getValueText(rawValue, bindingData);

                if (xValue < 0) {
                    return xScale(0) - d3Utils.getTextWidth(valueText, svg, VALUE_TEXT_FONT_SIZE) - VALUE_TEXT_MARGIN.horizontal;
                }

                return xScale(0) + VALUE_TEXT_MARGIN.horizontal;
            });


        //
        // Remove Old Bars
        //
        bars.exit()
                .remove();
    };

    var renderXAxis = function (element, xScale, xTickFormat) {
        var isAxisCreated = function (element) {
            return $("svg.x.axis", element).length > 0;
        };

        var ticks = 3;
        if (xTickFormat === "n0") {
            ticks = d3.min([ticks, d3.max(xScale.domain())]);
        }

        var xAxis = d3.svg.axis()
            .scale(xScale)
            .ticks(ticks)
            .tickFormat(function(datum) { return ko.i18n.format(datum, xTickFormat); })
            .orient("bottom");

        var $element = $(element);

        if (!isAxisCreated(element)) {
            // Create the nodes that the axis will live in
            var xAxisNode = d3.select(element)
                                .append("svg")
                                     .attr("class", "x axis")
                                     .attr("width", $element.width() - domUtils.getScrollbarWidth())
                                     .attr("height", X_AXIS_HEIGHT);

            xAxisNode.append("g")
                  .attr("transform", "translate(" + (LABEL_WIDTH + LABEL_MARGIN) + ",0)")
                  .call(xAxis);

            var $xAxisTitleNode = $(".x-axis.axis-title", element);
            $(xAxisNode[0]).insertBefore($xAxisTitleNode);

        } else {
            // Update the axis
            d3.select(element).select(".x.axis g")
                .transition()
                .call(xAxis);
        }
    };

    var configureXScale = function (dataCollection, bindingData, width) {
        var values = _.map(dataCollection, function (obj) {
            return getValueFromDataObject(obj, bindingData.valueKey);
        });

        var minValue = d3.min(values);
        var maxValue = d3.max(values);

        // Adjust the values of minValue and maxValue to be zero if the values collection
        // is empty
        if (!_.isUndefined(minValue)) {
            minValue = Math.min(0, minValue);
        } else {
            minValue = 0;
        }

        if (!_.isUndefined(maxValue)) {
            maxValue = Math.max(0, maxValue);
        } else {
            maxValue = 0;
        }

        // Account for the consumer declaring a default domain in the binding data. If
        // the minValue or maxValue declared above is larger than the declared domain,
        // use those values. Otherwise, the declared domain takes precendence
        if (bindingData.xAxis && bindingData.xAxis.domain) {
            minValue = Math.min(minValue, bindingData.xAxis.domain[0]);
            maxValue = Math.max(maxValue, bindingData.xAxis.domain[1]);
        }

        // If minValue is negative, the bar chart becomes a split bar chart. When this
        // occurs, we take the domain value that is furthest from zero and make that the
        // max and min. This ensures that "0" is always in the middle of the x-axis.
        if (minValue < 0) {
            maxValue = Math.max(Math.abs(minValue), maxValue);
            minValue = maxValue * -1;
        }

        return d3.scale.linear()
            .domain([minValue, maxValue])
            .range([0, width]);
    };

    /**
     * Create a bar chart from a collection of data. This bar chart will display horizontal
     * bars whose canvas can scroll vertically while the x-axis stays fixed in its location.
     *
     * **TODO**: As of 5/22/14, the arguments "valueKey" and "categoryTextKey" are required
     * when they should be completely optional. As such, to get this binidnig handler to work,
     * you need to pass in a complex object with an attribtue that contains a label string
     * and an attribute that contains a value string. THIS NEEDS TO CHANGE - this binding
     * should support the ability to pass in an array of basic values to create bars. In
     * this case, the labels should be the index number of value's spot in the collection.
     *
     * **TODO**: As of 5/22/14, logic should be added to make the chart more responsive to
     * fluid layouts.
     *
     * **TODO**: As of 5/22/14, Account for when the data attribute is empty. It should
     * render the canvas and the axes, but the ticks and content should be empty.
     *
     * @param {Array|Object} data - An array containing the collection of data to chart.
     *  If an object is provided, then the attributes of the object will be used to
     *  configure optional settings on the chart.
     *
     * @param {Object[]} options.data - An array containing the collection of data to plot.
     *  If the items in the array are arbitrary Javascript objects, you can utilize the
     *  'valueKey' argument to define which attribute contains the data's value.
     *
     * @param {String} [options.valueKey=undefined] A string that represents the attribute
     *  key on each object in the data array that should be used as the value to display.
     *
     * @param {String} [options.categoryTextKey=undefined] - A string that represents the
     *  attribute key on each object in the data array that should be used as the text to
     *  display for each category in the chart.
     *
     * @param {Object} [options.xAxis=undefined] An object containing settings for the
     *  x-axis of the chart.
     *
     * @param {Date[]} [options.xAxis.domain=undefined] An array that contains two items
     *  that represent the minimum and maximum values of the domain that should be used to
     *  display in the x-axis of the chart. These domains will be used as the "default"
     *  values of the domain. If the domain from the collection of data, then this
     *  declaration will be overridden. If the domain from the collection of values is
     *  smaller than the default domain, the default domain will take precedence.
     *
     * @param {String} [options.xAxis.title=undefined] - The message key to use for
     *  the title of the chart's X axis
     *
     * @param {String} [options.xAxis.format="n"] - The format for the numbers rendered
     * on the x-axis of the chart.
     *
     * @param {Object} [options.yAxis=undefined] An object containing settings for the
     *  y-axis of the chart
     *
     * @param {String} [options.yAxis.title=undefined] - The message key to use for
     *  the title of the chart's Y axis.
     *
     * @param {Object} [options.event=undefined] An object containing settings for
     *  user events that occur in the chart.
     * @param {Function} [options.event.scroll] A callback function that will be called
     *  whenever the user scrolls near canvas to the bottom of its container. NOTE: UX
     *  has final say as to whether using scroll events to incoporate an infinite scroll
     *  interaction should be done.
     *
     * @param {Function} [options.formatValueText] A function that will be used to generate
     *  the textual representation of the value.
     */
    ko.bindingHandlers.barChart = {
        init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
            var bindingData = ko.unwrap(valueAccessor());

            var $element = $(element);
            var $yAxisTitleNode;

            $element.addClass("chart chart-bar");

            //
            // Insert the axis title nodes
            //

            if (bindingData.yAxis && bindingData.yAxis.title) {
                $yAxisTitleNode = $("<div/>", {
                    "class": "axis-title",
                    "text": ko.i18n.renderString(bindingData.yAxis.title)
                }).prependTo($element);
            }

            if (bindingData.xAxis && bindingData.xAxis.title) {
                $("<div/>", {
                    "class": "axis-title x-axis",
                    "text": ko.i18n.renderString(bindingData.xAxis.title)
                }).css({
                    "margin-left": (LABEL_WIDTH + LABEL_MARGIN) + "px"
                }).appendTo($element);
            }

            //
            // Create and insert the scrollable chart canvas
            //

            var canvasNode = $("<div/>", {
                "class": CANVAS_CLASS
            });

            if ($yAxisTitleNode) {
                canvasNode.insertAfter($yAxisTitleNode);
            } else {
                $element.prepend(canvasNode);
            }

            // Hook up the canvas container to the scrolled event
            if (bindingData.event && bindingData.event.scroll) {
                ko.bindingHandlers.scrolled.init(
                    canvasNode[0],
                    function () {
                        return bindingData.event.scroll;
                    },
                    allBindings,
                    viewModel,
                    bindingContext
                );
            }
        },

        update: function (element, valueAccessor) {
            var $element = $(element);
            var chartContentContainer = $("." + CANVAS_CLASS, element)[0];

            var bindingData = ko.unwrap(valueAccessor());
            var seriesData = _.isPlainObject(bindingData) ? ko.unwrap(bindingData.data) : bindingData;

            if (seriesData.length === 0) {
                return;
            }

            var labels = _.map(seriesData, function (obj) {
                return getCategoryTextFromDataObject(obj, bindingData.categoryTextKey);
            });

            //
            // Dimension Calculations
            //
            var CONTENT_HEIGHT = (BAR_HEIGHT + BAR_MARGIN.bottom) * seriesData.length;

            var LINE_HEIGHT = Math.max(CONTENT_HEIGHT, $(chartContentContainer).height());

            var MAX_BAR_WIDTH =
                    $element.width() -
                    domUtils.getScrollbarWidth() -
                    LABEL_WIDTH -
                    LABEL_MARGIN -
                    LABEL_TICK_LINE_LENGTH;

            //
            // Configure Scales
            //

            var x = configureXScale(seriesData, bindingData, MAX_BAR_WIDTH);

            var y = d3.scale.ordinal()
                        .domain(labels)
                        .rangeRoundBands([0, CONTENT_HEIGHT], 0.4, 0);

            //
            // Render Chart Content
            //

            var xTickFormat = "n";
            if(bindingData.xAxis && bindingData.xAxis.format) {
                xTickFormat = bindingData.xAxis.format;
            }
            renderXAxis(element, x, xTickFormat);

            //
            // Generate Chart Canvas
            //

            var barChartSvg = renderSvgContainer(chartContentContainer, CONTENT_HEIGHT);

            renderVerticalLines(barChartSvg, LINE_HEIGHT, x);

            renderBars(barChartSvg, seriesData, x, y, bindingData);
            renderLabels(barChartSvg, seriesData, y, bindingData);

        }
    };

});

